import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useChannelConnection, useUpdateChannelConnection } from '@/lib/api/channel-connections';
import { ApiError } from '@/lib/api/client';
import { ConnectionForm } from '@/routes/gateway/connection-form';

export function ConnectionEditPage({ connectionId }: { connectionId: string }) {
  const connectionQuery = useChannelConnection(connectionId);
  const updateConnection = useUpdateChannelConnection(connectionId);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (connectionQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (connectionQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load connection: {connectionQuery.error.message}
      </p>
    );
  }

  const connection = connectionQuery.data;
  const backToDetail = () =>
    void navigate({ to: '/gateway/connections/$connectionId', params: { connectionId } });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Edit connection</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">{connection.handle}</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/gateway/connections">Back</Link>
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>Leave a secret blank to keep its stored value.</CardDescription>
        </CardHeader>
        <CardContent>
          <ConnectionForm
            initial={{
              channel: connection.channel,
              externalAccountId: connection.externalAccountId,
              externalActorId: connection.externalActorId ?? '',
              handle: connection.handle,
              oauthClientId: connection.oauthClientId ?? '',
              enabled: connection.enabled,
              inboxEnabled: connection.inboxEnabled,
            }}
            secretState={{
              verifyTokenSet: connection.verifyTokenSet,
              appSecretSet: connection.appSecretSet,
              accessTokenSet: connection.accessTokenSet,
              oauthClientSecretSet: connection.oauthClientSecretSet,
            }}
            submitting={updateConnection.isPending}
            submitLabel="Save connection"
            error={error}
            onCancel={backToDetail}
            onSubmit={(input) => {
              setError(null);
              updateConnection.mutate(input, {
                onSuccess: backToDetail,
                onError: (caught) =>
                  setError(
                    caught instanceof ApiError ? caught.message : 'Could not save the connection.',
                  ),
              });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
